import { MainService } from 'api/schema'
import {
  ROBOT_COMMANDS,
  ROBOT_EQUIPMENT_COMMAND,
  ROBOT_EQUIPMENT_STATES,
  ROBOT_LOCALIZATION_COMMAND,
  ROBOT_MODES,
  ROBOT_NETWORK_STATUSES,
  ROBOT_STATUSES,
  TASK_STATUSES,
} from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'
import { readLiveExecutionFromRobotRead } from 'utils/robotLiveExecution'
import { getRobotTypeDisplayName } from 'utils/robotTypeDisplayName'
import { mapCatalogOperationFromServer } from 'utils/selectCatalogOperation'
import { VENDOR } from 'utils/vendorDisplayName'
import type { VendorCode } from 'utils/vendorDisplayName'
import { mapActiveModeMetadata } from 'utils/wsTelemetryPatch'

import { Odyssey, RobotRead, SchedulerSettings, TaskTypeEnum } from './schema'

type RobotReadWithTimezone = RobotRead & { timezone?: string | null }

export interface RobotTypeDefinitionItem {
  id: number
  code: string
  display_name: string
  vendor_code: string
  vendor_display_name: string
}

/** Mission.robot_type value for API (Sweeper, Combo, ComboOmni). Only Autonomics types are used in missions. */
export interface MissionRobotTypeOption {
  display_name: string
  robot_type: string
}

const AUTONOMICS_MISSION_CODES = new Set(['Sweeper', 'Combo', 'ComboOmni'])

/** Catalog service-point ref (OperatingMode 15 / 16 / 17). Wire: kind uint8 + action_type uint8[]. */
export interface ServicePointCalibMetadata {
  kind: 2 | 3
  action_type?: number[]
  /** REST convenience — accepted by API, not sent on wire when kind is set */
  target?: 'station_lot' | 'maintenance_point'
  station_id?: string
  lot_id?: number
  lot_model?: string
  maintenance_point_id?: number
}

// Metadata for robot mode switches. Different modes use different subsets.
export interface RobotModeMetadata {
  name?: string | null
  pose?: {
    lon: number
    lat: number
    yaw: number // In degrees, range (-180, 180], 0 = east
  } | null
  continue_on_the_facility?: boolean
  update_map?: boolean
  facility_id?: string
  base_map?: boolean
  target?: ServicePointCalibMetadata['target']
  kind?: ServicePointCalibMetadata['kind']
  action_type?: number[]
  station_id?: string
  lot_id?: number
  lot_model?: string
  maintenance_point_id?: number
}

export interface GausiumExecutableTaskRecent {
  id: number
  robot_id: number
  robot_sn?: string
  vendor_task_uuid: string
  task_name: string
  vendor_map_id?: string
  map_name: string
  gausium_map_id?: number | null
  command_id?: string
  command_state?: string
  command_created_at?: string | null
  last_seen_at?: string | null
  /** First time this executable task row appeared in DB (differs per task). */
  created_at?: string | null
}

const normalizeRobot = (robotRaw: RobotRead): IRobotInfo => {
  type RobotJobInline = RobotRead['odysseys'][number]
  const liveExec = robotRaw.vendor === VENDOR.GAUSIUM ? readLiveExecutionFromRobotRead(robotRaw) : undefined
  const jobs: RobotJobInline[] = [...(robotRaw.odysseys || [])]
  /** Enrich from embedded job row when it matches `last_job_id` (same idea as b656545f: last_* is canonical). */
  const activeTaskRow: RobotJobInline | undefined = jobs.find(j => j.id === robotRaw.last_job_id)
  const normalizedActiveTask =
    robotRaw.last_job_id || liveExec
      ? {
          id: robotRaw.last_job_id || liveExec!.client_correlation_id,
          type: robotRaw.last_task_type ?? (activeTaskRow as any)?.job_type ?? 'Odyssey',
          missionSequenceNumber: robotRaw.last_mission_sequence_number,
          missionName:
            robotRaw.last_mission_name ||
            (activeTaskRow as any)?.name ||
            (activeTaskRow as any)?.mission_name ||
            liveExec?.task_name ||
            liveExec?.map?.name ||
            '',
          missionId:
            robotRaw.last_mission ||
            (activeTaskRow as any)?.mission_id ||
            (activeTaskRow as any)?.mission_status_last?.mission_progress?.mission_id ||
            0,
          status: ((activeTaskRow as any)?.status as TASK_STATUSES) || TASK_STATUSES.OK,
        }
      : undefined

  // Extract map_list from robotRaw
  // Ensure it's always an array or null/undefined, even if backend sends unexpected type
  const mapListRaw = (robotRaw as any).map_list
  const mapList = Array.isArray(mapListRaw) ? mapListRaw : mapListRaw ?? null

  const vendor = (robotRaw.vendor || VENDOR.AUTONOMICS) as VendorCode
  const networkStatus = (robotRaw.network_status as ROBOT_NETWORK_STATUSES) || ROBOT_NETWORK_STATUSES.OFFLINE
  /** Stale telemetry can keep danger=true after disconnect; GS offline => do not treat as emergency for UI/sort. */
  const emergencyFromTelemetry =
    robotRaw.equipment_state === ROBOT_EQUIPMENT_STATES.EMERGENCY ||
    (robotRaw as any).telemetry_last?.locks?.danger_button === true

  const pendingRaw = (
    robotRaw as { mode_switch_pending?: { mode?: number; requested_at?: string; timeout_sec?: number } | null }
  ).mode_switch_pending
  const modeSwitchPending =
    pendingRaw && pendingRaw.mode != null && pendingRaw.requested_at
      ? {
          mode: pendingRaw.mode as ROBOT_MODES,
          requestedAt: pendingRaw.requested_at,
          timeoutSec: pendingRaw.timeout_sec ?? 10,
        }
      : null

  const catalogServiceActionTypeRaw = (robotRaw as { catalog_service_action_type?: number | null })
    .catalog_service_action_type
  const catalogServiceActionType = catalogServiceActionTypeRaw != null ? Number(catalogServiceActionTypeRaw) : null

  const activeModeMetadataRaw = (robotRaw as { active_mode_metadata?: unknown }).active_mode_metadata
  const activeModeMetadata =
    activeModeMetadataRaw !== undefined ? mapActiveModeMetadata(activeModeMetadataRaw) : undefined

  const catalogOperationRaw = (robotRaw as { catalog_operation?: unknown }).catalog_operation
  const catalogOperation = mapCatalogOperationFromServer(catalogOperationRaw)

  const chassis = (
    robotRaw as {
      robot_type_definition?: {
        pose_frame?: string | null
        wheel_base_m?: number | string | null
        length_m?: number | string | null
        origin_to_front_m?: number | string | null
      }
    }
  ).robot_type_definition
  const finiteMeters = (value: number | string | null | undefined): number | null => {
    const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
    return Number.isFinite(n) ? n : null
  }

  return {
    id: robotRaw.id,
    serialNumber: robotRaw.serial_number,
    robot_type: robotRaw.robot_type,
    poseFrame: chassis?.pose_frame ?? null,
    wheelBaseM: finiteMeters(chassis?.wheel_base_m),
    lengthM: finiteMeters(chassis?.length_m),
    originToFrontM: finiteMeters(chassis?.origin_to_front_m),
    robot_type_display:
      getRobotTypeDisplayName(vendor, robotRaw.robot_type) || robotRaw.robot_type_display || robotRaw.robot_type,
    name: robotRaw.name || '',
    description: robotRaw.description || '',
    createdAt: robotRaw.created_at,
    updatedAt: robotRaw.updated_at,
    status: ROBOT_STATUSES.AVAILABLE,
    robotRaw: robotRaw,
    sim_card_number: robotRaw.sim_card_number || '',
    robot_color: robotRaw.robot_color || '',
    timezone: (robotRaw as RobotReadWithTimezone).timezone || null,
    activeMode: robotRaw.active_mode as ROBOT_MODES,
    activeCommand: robotRaw.active_command as ROBOT_COMMANDS,
    modeSwitchPending,
    catalogServiceActionType,
    catalogOperation,
    activeModeMetadata,
    activeTask: normalizedActiveTask,
    networkStatus,
    lastPosition: (() => {
      const telemetryPoint = robotRaw.telemetry_last_point
      // Same as geo_point below: odometry / GeoJSON may be 2D or 3D (lon, lat, z); only first two are for the map.
      if (
        telemetryPoint?.coordinates &&
        Array.isArray(telemetryPoint.coordinates) &&
        telemetryPoint.coordinates.length >= 2
      ) {
        const c = telemetryPoint.coordinates
        return {
          type: 'Point' as const,
          coordinates: [Number(c[0]), Number(c[1])] as [number, number],
        }
      }
      const geo = (robotRaw as any).geo_point as { type?: string; coordinates?: number[] } | undefined
      if (geo?.type === 'Point' && Array.isArray(geo.coordinates) && geo.coordinates.length >= 2) {
        return { type: 'Point' as const, coordinates: geo.coordinates as [number, number] }
      }
      return undefined
    })(),
    lastOrientation: robotRaw.telemetry_last_orientation as { x: number; y: number; z: number; w: number },
    vendor,
    facilityId: (robotRaw as any).facility_id,
    facilityName: (robotRaw as any).facility_name,
    map_list: mapList,
    batteryPower: robotRaw.battery_power,
    isCharging: (robotRaw as any).is_charging,
    lastTelemetryAt: (robotRaw as any).last_telemetry_at ?? null,
    systemDiagnostics: robotRaw.system_diagnostics,
    emergencyButton:
      vendor === VENDOR.GAUSIUM && networkStatus === ROBOT_NETWORK_STATUSES.OFFLINE ? false : emergencyFromTelemetry,
  }
}

export const RobotApi = {
  fetchRobotTypeDefinitions: async (vendorCode: VendorCode): Promise<RobotTypeDefinitionItem[]> => {
    const params = new URLSearchParams({ vendor__code: vendorCode })
    const response = await fetch(`/api/main/robot-type-definition/?${params}`, { credentials: 'include' })
    if (!response.ok) return []
    const data = await response.json()
    return data.results ?? data ?? []
  },

  /** Fetch robot type definitions for mission creation (only Autonomics). Returns options with display_name and robot_type for API. */
  fetchMissionRobotTypeOptions: async (): Promise<MissionRobotTypeOption[]> => {
    const items = await RobotApi.fetchRobotTypeDefinitions(VENDOR.AUTONOMICS)
    return items
      .filter(item => AUTONOMICS_MISSION_CODES.has(item.code))
      .map(item => ({
        display_name: getRobotTypeDisplayName(VENDOR.AUTONOMICS, item.code) || item.display_name,
        robot_type: item.code,
      }))
  },
  fetchRobots: async (
    page?: number,
    pageSize?: number,
    search?: string,
    ids?: number[],
    vendor?: string,
    onCancel?: (cancelFn: () => void) => void,
    compact?: boolean
  ): Promise<{ results: IRobotInfo[]; count: number }> => {
    try {
      const params = new URLSearchParams()
      if (page) params.append('page', String(page))
      if (pageSize) params.append('page_size', String(pageSize))
      if (search) params.append('search', search)
      if (vendor) params.append('vendor', vendor)
      if (ids && ids.length) {
        ids.forEach(id => params.append('id', String(id)))
      }
      if (compact) params.append('compact', 'true')

      const controller = new AbortController()
      if (onCancel) {
        onCancel(() => controller.abort())
      }

      const response = await fetch(`/api/main/robot/?${params}`, {
        credentials: 'include',
        signal: controller.signal,
      })

      if (!response.ok) {
        throw new Error('Failed to fetch robots')
      }

      const robots = await response.json()
      if (!robots.results) {
        return { results: [], count: 0 }
      }
      const normalizedRobots = robots.results.map((robotRaw: any) => normalizeRobot(robotRaw))
      return { results: normalizedRobots, count: robots.total || robots.count || 0 }
    } catch (error: any) {
      if (error && error.name === 'AbortError') {
        const err = new Error('Request cancelled')
        err.name = 'CancelError'
        throw err
      }
      if (error instanceof TypeError && (error.message === 'Failed to fetch' || error.message.includes('network'))) {
        throw error
      }
      console.error('[RobotApi] fetchRobots error:', error)
      throw error
    }
  },
  createRobot: async (
    name: string,
    description: string,
    serialNumber: string,
    sim_card_number?: string | null,
    robotType?: string | null,
    robotColor?: string | number | null,
    vendor?: VendorCode
  ): Promise<boolean> => {
    const body: Record<string, unknown> = {
      name,
      description,
      users_ids: [],
      serial_number: serialNumber,
      sim_card_number: sim_card_number,
      robot_type: robotType,
      robot_color: robotColor,
      vendor: vendor || VENDOR.AUTONOMICS,
    }
    const robot = await MainService.mainRobotCreate(body as any)
    if (robot) {
      return true
    }
    return false
  },
  patchRobot: async (
    id: number,
    name: string,
    description: string,
    sim_card_number?: string | null,
    robotType?: string | null,
    robotColor?: string | number | null,
    vendor?: VendorCode,
    facilityId?: number | null
  ): Promise<boolean> => {
    const body: Record<string, unknown> = {
      name,
      description,
      sim_card_number: sim_card_number,
    }
    if (robotType !== undefined) body.robot_type = robotType
    if (robotColor !== undefined) body.robot_color = robotColor
    if (vendor !== undefined) body.vendor = vendor
    if (facilityId !== undefined) body.facility_id = facilityId
    const robot = await MainService.mainRobotPartialUpdate(id, body as any)
    if (robot) {
      return true
    }
    return false
  },
  fetchRobot: async (id: number): Promise<IRobotInfo> => {
    try {
      const robotRaw = await MainService.mainRobotRetrieve(id)

      // Check if robotRaw is valid before normalization
      // If robotRaw is undefined after a successful request (status 200), it's likely a network issue
      // (ERR_NETWORK_CHANGED can return 200 but with invalid/empty data)
      if (!robotRaw) {
        // Treat as network error - re-throw as TypeError to be handled by callers
        const networkError = new TypeError('Failed to fetch')
        throw networkError
      }

      const robot = normalizeRobot(robotRaw)
      return robot
    } catch (error) {
      // Network errors (ERR_NETWORK_CHANGED, Failed to fetch) are common and expected
      // when connection is unstable - don't log them as errors
      if (error instanceof TypeError && (error.message === 'Failed to fetch' || error.message.includes('network'))) {
        // Re-throw network errors silently - they will be handled by callers
        throw error
      }
      // Log other errors (server errors, validation errors, etc.)
      console.error('[RobotApi] fetchRobot error:', error)
      throw error
    }
  },
  deleteRobot: async (id: number): Promise<void> => {
    await MainService.mainRobotDestroy(id)
  },

  createOdyssey: async (
    ArrayMissionId: number[],
    robotId: number,
    scheduler_settings?: SchedulerSettings,
    name?: string,
    scenario?: number | null,
    settings?: Odyssey['settings']
  ): Promise<Odyssey> => {
    const task = await MainService.mainOdysseyCreate({
      missions: ArrayMissionId,
      robot_id: robotId,
      job_type: TaskTypeEnum.SCENARIO,
      scheduler_settings: scheduler_settings ?? null,
      name: name ?? null,
      scenario: scenario,
      settings: settings ?? null,
    })
    return task
  },

  updateTheEntireOdyssey: async (
    odyssey_id: number,
    missionIdArray: number[],
    robotId: number,
    scheduler_settings?: SchedulerSettings,
    name?: string,
    scenario?: number | null,
    status: TASK_STATUSES = TASK_STATUSES.NEW,
    settings?: Odyssey['settings']
  ): Promise<Odyssey> => {
    const updateData: any = {
      robot_id: robotId,
      missions: missionIdArray,
      name: name,
      scheduler_settings: scheduler_settings,
      status: status,
      scenario: scenario,
    }
    if (settings !== undefined) {
      updateData.settings = settings
    }
    const odyssey = await MainService.mainOdysseyPartialUpdate(odyssey_id, updateData)
    return odyssey
  },

  updateOdysseyType: async (
    task_id: number,
    job_type: TaskTypeEnum,
    status: TASK_STATUSES = TASK_STATUSES.NEW
  ): Promise<Odyssey> => {
    const odyssey = await MainService.mainOdysseyPartialUpdate(task_id, {
      job_type: job_type,
      status: status,
    })
    return odyssey
  },

  uploadOdysseyToRobot: async (odysseyId: number) => {
    await MainService.mainOdysseyStartCalculationCreate(odysseyId)
  },

  getNtripSettings: async (id: number) => {
    const response = await fetch(`/api/main/robot/${id}/ntrip-settings/`)
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to load NTRIP settings' }))
      throw new Error(errorData.detail || 'Failed to load NTRIP settings')
    }
    return (await response.json()) as {
      caster: string
      port: number | null
      user: string
      mountpoint: string
      password_set: boolean
    }
  },

  saveNtripSettings: async (
    id: number,
    body: { caster: string; port: number; user: string; mountpoint: string; password?: string }
  ) => {
    const response = await fetch(`/api/main/robot/${id}/ntrip-settings/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to save NTRIP settings' }))
      const detail = errorData.detail
      const message = Array.isArray(detail) ? detail[0] : detail || 'Failed to save NTRIP settings'
      throw new Error(typeof message === 'string' ? message : 'Failed to save NTRIP settings')
    }
    return response.json()
  },

  getNtripMountpoints: async (id: number, caster: string) => {
    const response = await fetch(`/api/main/robot/${id}/ntrip-mountpoints/?caster=${encodeURIComponent(caster)}`)
    if (!response.ok) {
      throw new Error('Failed to load NTRIP mountpoints')
    }
    const body = (await response.json()) as { mountpoints?: string[] }
    return Array.isArray(body.mountpoints) ? body.mountpoints : []
  },

  switchMode: async (id: number, mode: ROBOT_MODES, metadata?: RobotModeMetadata) => {
    const url = `/api/main/robot/${id}/switch-mode/${String(mode)}/`
    const options: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }

    // Add body only if metadata is provided
    // Empty body means legacy mode
    if (metadata) {
      options.body = JSON.stringify(metadata)
    }

    const response = await fetch(url, options)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to switch mode' }))
      throw new Error(errorData.detail || errorData.message || 'Failed to switch mode')
    }
  },
  execCommand: async (id: number, command: ROBOT_COMMANDS) => {
    await MainService.mainRobotExecCommandCreate(id, String(command))
  },
  execEquipmentCommand: async (id: number, command: ROBOT_EQUIPMENT_COMMAND, value: string): Promise<void> => {
    await MainService.mainRobotExecEquipmentCommandCreate(command, id, value)
  },

  execEquipmentWorkingType: async (id: number, workingType: number): Promise<void> => {
    const response = await fetch(`/api/main/robot/${id}/exec-equipment-working-type/${workingType}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to switch cleaning mode' }))
      throw new Error(errorData.detail || errorData.message || 'Failed to switch cleaning mode')
    }
  },

  execEquipmentDeviceSettings: async (id: number, device: 'turbine' | 'scrubber_brush', rpm: number): Promise<void> => {
    const response = await fetch(`/api/main/robot/${id}/exec-equipment-device-settings/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ device, rpm }),
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to set device rpm' }))
      const detail = errorData.detail
      const message = Array.isArray(detail) ? detail[0] : detail || errorData.message || 'Failed to set device rpm'
      throw new Error(typeof message === 'string' ? message : 'Failed to set device rpm')
    }
  },

  asbisTask: async (id: number, action: 'launch' | 'cancel'): Promise<{ active_mode: number; mqtt: boolean }> => {
    const response = await fetch(`/api/main/robot/${id}/asbis-task/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ action }),
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to send Asbis task command' }))
      throw new Error(errorData.detail || errorData.message || 'Failed to send Asbis task command')
    }
    return response.json()
  },

  execLocalizationCommand: async (
    id: number,
    command: ROBOT_LOCALIZATION_COMMAND,
    metadata?: { pose?: { lon: number; lat: number; yaw: number } }
  ): Promise<void> => {
    await fetch(`/api/main/robot/${id}/exec-localization-command/${command}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(metadata ?? {}),
    })
  },

  fetchGausiumExecutableTasksRecent: async (id: number): Promise<GausiumExecutableTaskRecent[]> => {
    const response = await fetch(`/api/main/robot/${id}/gausium-executable-tasks-present/`, {
      credentials: 'include',
    })
    if (!response.ok) return []
    const data = await response.json()
    return Array.isArray(data) ? data : []
  },

  fetchGausiumExecutableTasksPresentAll: async (): Promise<GausiumExecutableTaskRecent[]> => {
    const response = await fetch(`/api/main/robot/gausium-executable-tasks-present/`, {
      credentials: 'include',
    })
    if (!response.ok) return []
    const data = await response.json()
    return Array.isArray(data) ? data : []
  },

  normalizeRobotMapLayer: (layerRaw: any) => {
    if (!layerRaw) return undefined
    return {
      id: layerRaw.id,
      bbox:
        layerRaw.bbox && layerRaw.bbox.coordinates
          ? {
              type: 'Polygon',
              coordinates: layerRaw.bbox.coordinates,
            }
          : undefined,
      urlTiles: layerRaw.file_tiles || undefined,
      name: layerRaw.name || '',
      status: layerRaw.status,
      robotId: layerRaw.robot_id,
    }
  },

  fetchRobotMapLayers: async (robotId: number, name: string): Promise<any> => {
    const params = new URLSearchParams({ robot_id: String(robotId), name })
    const response = await fetch(`/api/main/robot-map-layer/?${params}`, { credentials: 'include' })
    if (!response.ok) return { results: [] }
    const data = await response.json()
    return {
      ...data,
      results: (data.results || []).map((layer: any) => RobotApi.normalizeRobotMapLayer(layer)),
    }
  },

  fetchRobotMapLayer: async (id: number): Promise<any> => {
    const response = await fetch(`/api/main/robot-map-layer/${id}/`, { credentials: 'include' })
    if (!response.ok) throw new Error('Failed to fetch robot map layer')
    return RobotApi.normalizeRobotMapLayer(await response.json())
  },

  createRobotMapLayer: async (data: { robot_id: number; name: string }): Promise<any> => {
    const response = await fetch(`/api/main/robot-map-layer/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include',
    })
    if (!response.ok) throw new Error('Failed to create robot map layer')
    return RobotApi.normalizeRobotMapLayer(await response.json())
  },

  linkRobotMapLayer: async (id: number, filename: string): Promise<any> => {
    const response = await fetch(`/api/main/robot-map-layer/${id}/link-map/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename }),
      credentials: 'include',
    })
    if (!response.ok) {
      const err = new Error('Failed to link robot map layer') as Error & { status?: number }
      err.status = response.status
      throw err
    }
    return RobotApi.normalizeRobotMapLayer(await response.json())
  },
}
